import React, { useState, useEffect } from "react";
import { Table, Tooltip, Tag, Button, Spin, Typography, Empty, Alert } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import update from "immutability-helper";
import { TestRunService } from "../../services/TestRunService";
import { TestRunRequest, ChatCompletionRequest, ChatMessage } from "../../model_gen/evaluation";
import RequestView from "../shared/RequestView";
import { EyeOutlined, LoadingOutlined, DiffOutlined, MenuOutlined } from "@ant-design/icons";

const { Text } = Typography;

interface TestRunComparisonProps {
  testRunIds: number[];
  apiKey: string;
}

interface TestRunResult {
  chat_completion_request_test_id: number;
  original_request_id: string;
  original_request: ChatCompletionRequest;
  test_run_requests: {
    [testRunId: string]: {
      request: TestRunRequest;
      model: string;
    };
  };
}

interface TableDataItem {
  key: string;
  requestId: string;
  originalRequest: ChatCompletionRequest;
  chatCompletionRequestTestId: number;
  [testRunId: string]: { request: TestRunRequest; model: string; } | null | string | number | ChatCompletionRequest;
}

interface DraggableHeaderCellProps {
  index: number;
  moveColumn: (dragIndex: number, hoverIndex: number) => void;
  children: React.ReactNode;
}

const DraggableHeaderCell: React.FC<DraggableHeaderCellProps> = ({ index, moveColumn, children }) => {
  const [{ isDragging }, drag] = useDrag({
    type: "DraggableColumn",
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: "DraggableColumn",
    hover(item: { index: number }) {
      if (item.index !== index) {
        moveColumn(item.index, index);
        item.index = index;
      }
    },
  });

  return (
    <div
      ref={(node) => drag(drop(node))}
      style={{
        opacity: isDragging ? 0.5 : 1,
        cursor: "move",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <MenuOutlined style={{ marginRight: 8, color: "#999" }} />
      {children}
    </div>
  );
};

const TestRunComparison: React.FC<TestRunComparisonProps> = ({ testRunIds, apiKey }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [comparisonData, setComparisonData] = useState<TestRunResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [comparisonDataByPage, setComparisonDataByPage] = useState<Record<string, TestRunResult[]>>({});
  const [columnOrder, setColumnOrder] = useState<number[]>([]);
  
  // Static page size
  const pageSize = 10;

  // Reset state when testRunIds change
  useEffect(() => {
    setComparisonData([]);
    setComparisonDataByPage({});
    setCurrentPage(1);
    setError(null);
    setTotalCount(0);
    setColumnOrder(testRunIds);
  }, [testRunIds]);

  // Fetch comparison data for the current page
  useEffect(() => {
    if (!testRunIds || testRunIds.length === 0) {
      return;
    }

    // Create a cache key that includes both page and size
    const cacheKey = `${currentPage}-${pageSize}`;
    
    if (comparisonDataByPage[cacheKey]) {
      // Use cached data
      setComparisonData(comparisonDataByPage[cacheKey]);
    } else {
      // Fetch new data
      fetchComparisonData(currentPage);
    }
  }, [currentPage, testRunIds]);

  const fetchComparisonData = (page: number) => {
    const offset = (page - 1) * pageSize;
    setLoading(true);

    
    // Add a small delay to ensure we can see the logs in sequence
    setTimeout(() => {
      TestRunService.getTestRunsComparison(testRunIds, offset, pageSize, setLoading, apiKey).subscribe(
        (response: { items: TestRunResult[]; total: number }) => {
          
          const newData = response?.items || [];
          setComparisonData(newData);
          setTotalCount(response?.total || 0);
          
          // Use a cache key that includes both page and size
          const cacheKey = `${page}-${pageSize}`;
          
          setComparisonDataByPage(prev => ({
            ...prev,
            [cacheKey]: newData
          }));
          
          setLoading(false);
        },
        (error: Error) => {
          console.error("Error fetching test run comparison data:", error);
          setError("Failed to load comparison data: " + (error.message || "Unknown error"));
          setLoading(false);
        }
      );
    }, 100);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "#52c41a"; // green
    if (score >= 60) return "#faad14"; // yellow
    return "#f5222d"; // red
  };

  const renderScore = (testRunRequest: TestRunRequest | null) => {
    if (!testRunRequest || 
        !testRunRequest.qualitative_evaluation_results || 
        !Array.isArray(testRunRequest.qualitative_evaluation_results) ||
        testRunRequest.qualitative_evaluation_results.length === 0) {
      return <Text type="secondary">N/A</Text>;
    }

    const results = testRunRequest.qualitative_evaluation_results;
    const maxPossibleScore = results.length * 5; // Each result can score max 5
    const totalScore = results.reduce(
      (sum, result) => sum + (result.score_value || 0),
      0
    );
    
    // Avoid division by zero
    const normalizedScore = maxPossibleScore > 0 
      ? Math.round((totalScore / maxPossibleScore) * 100) 
      : 0;

    return (
      <Tooltip
        title={
          <div>
            {results.map((result, idx) => (
              <div key={idx} style={{ marginBottom: "8px" }}>
                {result.criteria}: {result.score_value}/5
                {result.description && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#ccc",
                    }}>
                    {result.description}
                  </div>
                )}
              </div>
            ))}
          </div>
        }>
        <Tag
          style={{
            color: getScoreColor(normalizedScore),
            borderColor: getScoreColor(normalizedScore),
            background: "transparent",
          }}>
          {normalizedScore}%
        </Tag>
      </Tooltip>
    );
  };

  // Render responses comparison tooltip
  const renderResponsesComparison = (record: TableDataItem) => {
    if (!record.originalRequest) {
      return "No original request found";
    }

    // Get original response - match exactly how RequestView.tsx gets it
    let originalResponse = "No response";
    
    // First check for _maitai_test_request_response in metadata (this is the edited/corrected response)
    const testResponse = record.originalRequest.metadata?._maitai_test_request_response;
    if (testResponse !== undefined && testResponse !== null) {
      originalResponse = testResponse as string;
    } 
    // If no test response, get the last assistant message content
    else if (record.originalRequest.params?.messages) {
      const messages = record.originalRequest.params.messages;
      // Find the last assistant message
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "assistant" && messages[i].content) {
          originalResponse = messages[i].content;
          break;
        }
      }
    }

    // Get test run responses in the order of columnOrder
    const testRunResponses = columnOrder.map(testRunId => {
      const testRunData = record[testRunId.toString()] as { request: TestRunRequest; model: string; };
      if (testRunData?.request?.response) {
        return {
          testRunId: testRunId.toString(),
          response: testRunData.request.response.choices?.[0]?.message?.content || "No response"
        };
      }
      return null;
    }).filter((item): item is { testRunId: string; response: string; } => item !== null);

    return (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "900px",
          maxWidth: "900px",
          boxSizing: "border-box",
        }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: testRunResponses.length > 0 
              ? `repeat(${testRunResponses.length + 1}, 1fr)` 
              : "1fr 1fr",
            gap: "16px",
            marginTop: "12px",
          }}>
          <div>
            <strong style={{ marginLeft: "12px", marginBottom: "8px", display: "flex", justifyContent: "space-between" }}>
              <span>
                Original Response
                {record.originalRequest.metadata?._maitai_test_request_response && "*"}
              </span>
            </strong>
            <div
              style={{
                padding: "12px",
                maxHeight: "400px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "14px",
                lineHeight: "1.6",
              }}>
              {originalResponse}
            </div>
          </div>
          
          {testRunResponses.map(({ testRunId, response }) => (
            <div key={testRunId}>
              <strong style={{ marginLeft: "12px", marginBottom: "8px" }}>
                Run {testRunId} Response
              </strong>
              <div
                style={{
                  padding: "12px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontSize: "14px",
                  lineHeight: "1.6",
                }}>
                {response}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const moveColumn = (dragIndex: number, hoverIndex: number) => {
    const dragColumn = columnOrder[dragIndex];
    setColumnOrder(
      update(columnOrder, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragColumn],
        ],
      })
    );
  };

  // Prepare columns for the table
  const getColumns = (): ColumnsType<TableDataItem> => {
    const baseColumns: ColumnsType<TableDataItem> = [
      {
        title: "Request ID",
        dataIndex: "requestId",
        key: "requestId",
        fixed: "left" as const,
        width: 150,
        render: (text: string, record: TableDataItem) => {
          if (!text) return <Text type="secondary">Unknown</Text>;
          
          const shortRequestId = text.length > 4 ? `...${text.slice(-4)}` : text;
          
          return (
            <Tooltip
              title={
                record.originalRequest ? (
                  <RequestView
                    request={record.originalRequest}
                    apiKey={apiKey}
                    systemPrompt={
                      record.originalRequest?.params?.messages?.find(
                        (m: ChatMessage) => m.role === "system"
                      )?.content || ""
                    }
                  />
                ) : (
                  "Request details not available"
                )
              }
              placement="right"
              overlayStyle={{ maxWidth: "600px", width: "600px" }}>
              <Button type="link" icon={<EyeOutlined />}>
                {shortRequestId}
              </Button>
            </Tooltip>
          );
        },
      },
    ];

    // Add columns for each test run ID in the current order
    const runColumns = columnOrder.map((testRunId, index) => ({
      title: () => {
        const model = comparisonData[0]?.test_run_requests[testRunId]?.model;
        return (
          <DraggableHeaderCell index={index} moveColumn={moveColumn}>
            <div style={{ textAlign: "center" }}>
              <div style={{ marginBottom: "4px" }}>Run {testRunId}</div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                {model || "Unknown"}
              </div>
            </div>
          </DraggableHeaderCell>
        );
      },
      dataIndex: testRunId.toString(),
      key: testRunId.toString(),
      width: 100,
      align: "center" as const,
      onHeaderCell: () => ({
        style: {
          verticalAlign: "top",
          paddingTop: "8px"
        }
      }),
      render: (testRunData: { request: TestRunRequest; model: string; } | null) => 
        renderScore(testRunData?.request || null),
    }));

    // Add actions column
    const actionsColumn = {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center" as const,
      render: (_: any, record: TableDataItem) => (
        <Tooltip
          title={renderResponsesComparison(record)}
          placement="left"
          overlayStyle={{ maxWidth: "900px", width: "900px" }}>
          <Button
            type="link"
            icon={<DiffOutlined />}
            onClick={(e) => e.stopPropagation()}>
            Responses
          </Button>
        </Tooltip>
      ),
    };

    return [...baseColumns, ...runColumns, actionsColumn];
  };

  // Prepare data for the table
  const getTableData = (): TableDataItem[] => {
    if (!comparisonData || comparisonData.length === 0) {
      return [];
    }

    return comparisonData.map((item) => {
      const rowData: TableDataItem = {
        key: item.chat_completion_request_test_id.toString(),
        requestId: item.original_request_id || "Unknown",
        originalRequest: item.original_request,
        chatCompletionRequestTestId: item.chat_completion_request_test_id,
      };

      // Add test run results to the row
      if (item.test_run_requests) {
        Object.entries(item.test_run_requests).forEach(([testRunId, testRunData]) => {
          rowData[testRunId] = testRunData;
        });
      }

      return rowData;
    });
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  const columns = getColumns();
  const tableData = getTableData();

  return (
    <DndProvider backend={HTML5Backend}>
      <div>
        <Spin spinning={loading} indicator={antIcon} tip="Loading comparison data...">
          <div style={{ minHeight: loading ? "200px" : "auto" , marginTop: "20px"}}>
            {!loading && tableData.length === 0 ? (
              <Empty description="No comparison data available" />
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalCount,
                      onChange: handlePageChange,
                      showSizeChanger: false,
                      position: ["bottomRight"]
                    }}
                    scroll={{ x: "max-content" }}
                    size="small"
                    bordered
                  />
                </div>
              </>
            )}
          </div>
        </Spin>
      </div>
    </DndProvider>
  );
};

export default TestRunComparison; 